import { useState, useCallback } from 'react';
import { fileApi } from '../api/fileApi';

export const useFileUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);

  const uploadFile = useCallback(async (file) => {
    setUploading(true);
    setProgress(0);
    setError(null);
    setUploadedFile(null);
    
    try {
      const onProgress = (percentCompleted) => {
        setProgress(percentCompleted);
      };

      const result = await fileApi.uploadFile(file, onProgress);
      
      setUploadedFile(result);
      setProgress(100);
      setUploading(false);
      
      return result;
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Upload failed');
      setProgress(0);
      setUploading(false);
      throw error;
    }
  }, []);

  const resetUpload = useCallback(() => {
    setUploading(false);
    setProgress(0);
    setUploadedFile(null);
    setError(null);
  }, []);

  return { 
    uploadFile, 
    uploading, 
    progress, 
    uploadedFile, 
    error, 
    resetUpload 
  };
};

export const useFileManagement = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    total: 0,
    pages: 0
  });

  const getFileList = useCallback(async (page = 1, perPage = 10, search = '') => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await fileApi.getFileList(page, perPage, search);
      setFiles(result.files || []);
      setPagination({
        page: result.page || 1,
        perPage: result.per_page || 10,
        total: result.total || 0,
        pages: result.pages || 0
      });
      return result;
    } catch (error) {
      console.error('Get file list error:', error);
      setError(error.message || 'Failed to get file list');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteFile = useCallback(async (fileId) => {
    try {
      await fileApi.deleteFile(fileId);
      // Remove file from local state
      setFiles(prev => prev.filter(file => file.file_id !== fileId));
      return { success: true };
    } catch (error) {
      console.error('Delete file error:', error);
      setError(error.message || 'Failed to delete file');
      throw error;
    }
  }, []);

  const updateFileStatus = useCallback(async (fileId, status) => {
    try {
      const result = await fileApi.updateFileStatus(fileId, status);
      // Update file status in local state
      setFiles(prev => prev.map(file => 
        file.file_id === fileId 
          ? { ...file, status: status }
          : file
      ));
      return result;
    } catch (error) {
      console.error('Update file status error:', error);
      setError(error.message || 'Failed to update file status');
      throw error;
    }
  }, []);

  const downloadFile = useCallback(async (fileId, filename) => {
    try {
      await fileApi.downloadFileToDevice(fileId, filename);
      return { success: true };
    } catch (error) {
      console.error('Download file error:', error);
      setError(error.message || 'Failed to download file');
      throw error;
    }
  }, []);

  const getFileInfo = useCallback(async (fileId) => {
    try {
      const result = await fileApi.getFileInfo(fileId);
      return result;
    } catch (error) {
      console.error('Get file info error:', error);
      setError(error.message || 'Failed to get file info');
      throw error;
    }
  }, []);

  const getMediaInfo = useCallback(async (fileId) => {
    try {
      const result = await fileApi.getMediaInfo(fileId);
      return result;
    } catch (error) {
      console.error('Get media info error:', error);
      setError(error.message || 'Failed to get media info');
      throw error;
    }
  }, []);

  return {
    files,
    loading,
    error,
    pagination,
    getFileList,
    deleteFile,
    updateFileStatus,
    downloadFile,
    getFileInfo,
    getMediaInfo
  };
};
